import { formatDate } from '@/_utils/date';
import { pluralizeNoun } from '@/_utils/utils';
import { useAppSelector } from '@/redux/hooks';
import { selectIsToolboxDrawerOpen, selectPackagingIsEditingTimeline, selectPackagingTimelineFrameRate } from '@/redux/selectors/packagingSelectors';
import { type TimelineItem } from '@/redux/slices/packagingSlice.types';
import styled from '@emotion/styled';
import { Divider as DividerMui, Fade, Typography } from '@mui/material';
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { LabelValue } from '../_common';
import PolarisIcon from '../_common/PolarisIcon';
import { getSMPTETimecode } from './utils';
import { isTimelineItemAssigned } from '@/components/Packaging/typeGuards';

type ActiveHoverDisplayProps = {
  data: TimelineItem[];
};

const LIMIT = 3;

const Divider = () => <DividerMui sx={{ margin: '8px 0' }} />;

const ActiveHoverDisplay = ({ data }: ActiveHoverDisplayProps) => {
  const frameRate = useAppSelector(selectPackagingTimelineFrameRate);
  const idEditingTimeline = useAppSelector(selectPackagingIsEditingTimeline);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { horizontal, vertical } = useHoverDisplayPlacement(wrapperRef);

  const itemsTypeName = data[0].type === 'segment' ? 'Segment' : 'Overlay';

  return (
    <Fade in={!idEditingTimeline}>
      <Wrapper ref={wrapperRef} horizontalPlacement={horizontal} verticalPlacement={vertical}>
        <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center' }}>
          <PolarisIcon
            icon={data[0].type === 'segment' ? 'cards' : 'layers'}
            height={data[0].type === 'segment' ? 22 : 17}
            width={data[0].type === 'segment' ? 22 : 17}
            style={{ marginRight: '8px', verticalAlign: 'middle' }}
          />
          {data.length === 1 ? itemsTypeName : pluralizeNoun(itemsTypeName)} Details
        </Typography>

        {data.length > LIMIT && (
          <>
            <Divider />
            <div>Showing details for the first {LIMIT} items. Zoom in on the timeline to see the remaining items.</div>
          </>
        )}

        {data.slice(0, LIMIT).map((item, index) => {
          const start = item.start;
          const end = item.end;
          const duration = end - start;
          const rightsWindowStart = undefined;
          const rightsWindowEnd = undefined;
          const usageWindowStart = isTimelineItemAssigned(item) ? item.source.use_window_start : undefined;
          const usageWindowEnd = isTimelineItemAssigned(item) ? item.source.use_window_end : undefined;

          return (
            <React.Fragment key={index}>
              <Divider />

              <LabelValue label="Cut Kind" horizontal>
                {item.label}
              </LabelValue>
              <LabelValue label="Rally Asset Name" horizontal>
                {item.subtitle}
              </LabelValue>
              <LabelValue label="Rights Window" horizontal>
                {rightsWindowStart && rightsWindowEnd ? (
                  <DateRange>
                    <span>{formatDate(rightsWindowStart)}</span>
                    <Typography>-</Typography>
                    <span>{formatDate(rightsWindowEnd)}</span>
                  </DateRange>
                ) : (
                  <span>-</span>
                )}
              </LabelValue>
              <LabelValue label="Usage Window" horizontal>
                {usageWindowStart && usageWindowEnd ? (
                  <DateRange>
                    <span>{usageWindowStart}</span>
                    <Typography>-</Typography>
                    <span>{usageWindowEnd}</span>
                  </DateRange>
                ) : (
                  <span>-</span>
                )}
              </LabelValue>
              <LabelValue label="Duration" horizontal>
                <Timestamp>
                  <div>{getSMPTETimecode(duration, frameRate)}</div>
                  <div>
                    {getSMPTETimecode(start, frameRate)} - {getSMPTETimecode(end, frameRate)}
                  </div>
                </Timestamp>
              </LabelValue>
            </React.Fragment>
          );
        })}
      </Wrapper>
    </Fade>
  );
};

export default ActiveHoverDisplay;

const useHoverDisplayPlacement = (element: RefObject<HTMLDivElement>) => {
  const isToolboxDrawerOpen = useAppSelector(selectIsToolboxDrawerOpen);
  const [horizontal, setHorizontal] = useState<'left' | 'right'>(isToolboxDrawerOpen ? 'right' : 'left');
  const vertical: 'top' | 'bottom' = isToolboxDrawerOpen ? 'top' : 'bottom';

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!element.current) return;

      const margin = 30;
      const elRect = element.current.getBoundingClientRect();

      if (horizontal === 'right') {
        if (e.clientX > elRect.left - margin) {
          setHorizontal('left');
        }
      } else if (horizontal === 'left') {
        if (e.clientX < elRect.left + elRect.width + margin) {
          setHorizontal('right');
        }
      }
    },
    [element, horizontal]
  );

  useEffect(() => {
    document.addEventListener('pointermove', handlePointerMove);

    return () => document.removeEventListener('pointermove', handlePointerMove);
  }, [handlePointerMove]);

  return {
    horizontal,
    vertical
  };
};

const Wrapper = styled.div<{ horizontalPlacement: 'left' | 'right'; verticalPlacement: 'top' | 'bottom' }>`
  position: fixed;
  height: fit-content;
  width: fit-content;
  background: ${({ theme }) => theme.palette.background.paper};
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing(2)};
  min-width: 240px;
  max-width: 40vw;
  box-shadow: ${({ theme }) => theme.shadows[5]};
  border-radius: 4px;
  user-select: none;
  pointer-events: none;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  row-gap: ${({ theme }) => theme.spacing(1)};

  ${({ horizontalPlacement, verticalPlacement, theme }) =>
    `
      ${verticalPlacement}: ${theme.spacing(10)};
      ${horizontalPlacement}: ${theme.spacing(2)};
    `}
`;

const Timestamp = styled.div`
  font-size: 0.9rem;
  width: 100%;

  div:first-of-type {
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    font-size: 1rem;
  }

  div:last-of-type {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const DateRange = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing(0.5)};
`;
